import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { FaBackward, FaEdit, FaSave } from "react-icons/fa";
import { Container } from "reactstrap";
import TextEditor from "../../../../components/TextEditor";
import { OptionsType } from "../Online Casino/AddNewCasino";
import ReactSelect from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import { BonusesAPI } from "../../../../api/bonuses";
import SPINNER from "../../../../components/Spinner";

interface SectionWrapperProps {
  children: React.ReactNode;
  header: string;
}
const SectionWrapper = (props: SectionWrapperProps) => {
  const { header, children } = props;
  return (
    <section className="w-full h-fit my-3 flex flex-col items-center">
      <h2 className="border-b pb-4 pl-3 mb-3 font-semibold text-gray-600 w-full">
        {header}
      </h2>
      <div className="w-full flex flex-col">{children}</div>
    </section>
  );
};

interface FormData {
  category: OptionsType;
  contentName: string;
  refURL: string;
  bonusCode: string;
  description: string;
  effectiveDate: string;
  picture: string;
  homeSequence: string;
  status: string;
}
const initialFormData: FormData = {
  category: { value: "Free Bonus", label: "Free Bonus" },
  contentName: "",
  refURL: "",
  description: "",
  bonusCode: "",
  effectiveDate: "",
  picture: "",
  homeSequence: "",
  status: "active",
};

const AddNewBonus = () => {
  document.title = "Casino24 - Add New Bonus";
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [picture, setPicture] = useState<File>();

  useEffect(() => {
    const fetchBonus = async () => {
      setLoading(true);
      if (state && state.id) {
        const res = await BonusesAPI.getBonus(state.id);
        if (res.data) {
          setFormData(res.data);
        }
      }
      setLoading(false);
    };
    fetchBonus();
  }, [state]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;

    if (name === "picture" && files && files.length > 0) {
      setPicture(files[0]);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: files && files.length > 0 ? files[0] : value,
      }));
    }
  };

  const handleEditorChange = (name: string, value: string) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleSelectChange = (selectedOptions: any, actionMeta: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [actionMeta.name]: selectedOptions,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    let pictureUrl = formData.picture;
    if (picture !== undefined) {
      const res = (await BonusesAPI.uploadImage(picture)) as {
        url?: string;
        success?: boolean;
      };
      if (res.success && res.url) {
        pictureUrl = res.url;
      } else alert("Something went wrong P. Try Again!");
    }

    const dataToSend = {
      category: formData.category,
      contentName: formData.contentName,
      refURL: formData.refURL,
      bonusCode: formData.bonusCode,
      description: formData.description,
      effectiveDate: formData.effectiveDate,
      picture: pictureUrl,
      homeSequence: formData.homeSequence,
      status: formData.status,
    };

    try {
      if (state && state.id) {
        await BonusesAPI.updateBonus(state.id, dataToSend);
        navigate("/vips/bonus-list");
      } else {
        const res = await BonusesAPI.saveBonus(dataToSend);
        if (res.data.id) {
          setFormData(initialFormData);
          navigate("/vips/bonus-list");
        }
      }
    } catch (error) {
      alert("Something went wrong D! Try Again.");
    }

    setLoading(false);
  };

  return (
    <Container
      fluid
      className="p-0 m-0 flex flex-col text-black w-full min-h-screen bg-[#eeeeee]  relative"
    >
      {/* Header */}
      <section className="p-4 text-[3xl] w-full h-fit bg-white font-bold">
        <h2>Add New Bonus</h2>
      </section>

      {/* Form Section */}
      <section className="flex-1 p-4 justify-center items-center">
        {/* Form */}
        <form
          className="bg-white h-full w-full flex flex-col p-3"
          onSubmit={handleSubmit}
        >
          {/* Informations */}
          <SectionWrapper header="Informations">
            {/* Category */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="category"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Select Category(s)
              </label>
              <ReactSelect
                id="category"
                name="category"
                value={formData.category}
                options={[
                  { value: "Deposit Bonus", label: "Deposit Bonus" },
                  { value: "Free Bonus", label: "Free Bonus" },
                ]}
                onChange={handleSelectChange}
                className="p-2 w-full text-[0.8rem] col-span-5"
              />
            </div>
            {/* Content Name */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="contentName"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Content Name
              </label>
              <input
                type="text"
                id="contentName"
                name="contentName"
                onChange={handleInputChange}
                value={formData.contentName}
                required
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Ref URL */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="refURL"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Ref URL
              </label>
              <input
                type="text"
                id="refURL"
                name="refURL"
                value={formData.refURL}
                onChange={handleInputChange}
                required
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/*  Description */}
            <div className="grid grid-cols-1 grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="description"
                className="block text-[0.8rem] text-gray-400 w-full items-start lg:justify-end md:px-3 flex"
              >
                Bonus Description
              </label>
              <div className="col-span-5">
                <TextEditor
                  name="description"
                  value={formData.description}
                  onChange={handleEditorChange}
                  height={400}
                  language="en"
                />
              </div>
            </div>
            {/* Bonus Code */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="bonusCode"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Bonus Code (If Any)
              </label>
              <input
                type="text"
                id="bonusCode"
                name="bonusCode"
                value={formData.bonusCode}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Effective Date */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="effectiveDate"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Effective Date
              </label>
              <input
                type="date"
                id="effectiveDate"
                name="effectiveDate"
                value={formData.effectiveDate}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Profile Picture */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="picture"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Profile Picture
              </label>
              <input
                type="file"
                id="picture"
                name="picture"
                accept="image/*"
                onChange={handleInputChange}
                required={formData.picture !== "" ? false : true}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {formData.picture !== "" && (
              <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
                <div />
                <div className="p-2 w-full col-span-5">
                  <img
                    alt={formData.contentName ?? "Preview"}
                    src={formData.picture}
                    className="w-64 h-64 object-cover"
                  />
                </div>
              </div>
            )}
            {/* Home Sequence */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="homeSequence"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Home Sequence
              </label>
              <input
                type="text"
                id="homeSequence"
                name="homeSequence"
                value={formData.homeSequence}
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/* Status */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="status"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
                className="p-2 w-full text-[0.8rem] col-span-5"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </SectionWrapper>

          <div className="flex justify-end items-center">
            <button
              type="submit"
              className="bg-green-600 text-white p-2 px-4 rounded-md mx-2 flex items-center"
            >
              {state && state.id ? (
                <>
                  <FaEdit className="mr-2" /> Update
                </>
              ) : (
                <>
                  <FaSave className="mr-2" /> Save
                </>
              )}
            </button>
            <button
              type="button"
              className="bg-gray-600 text-white p-2 px-4 rounded-md flex items-center"
              onClick={() => navigate("/vips/bonus-list")}
            >
              <FaBackward className="mr-2" /> Back
            </button>
          </div>
        </form>
      </section>
      {loading && (
        <div className="absolute inset-0 bg-gray-300/30 flex items-center justify-center backdrop-blur-sm">
          <SPINNER />
        </div>
      )}
    </Container>
  );
};

export default AddNewBonus;
